import { useEffect, useMemo, useState, type UIEvent } from 'react';
import type { StreamChat } from 'stream-chat';

import { Avatar } from '../components/Avatar.js';
import { Icon } from '../components/Icon.js';
import type { InboxItem } from '../fixtures/domain.js';
import { ListBadge } from '../inbox/ListBadge.js';
import { useInboxList } from '../data/inbox.js';
import type { InboxQuery } from '../inbox/query.js';
import { useListMembers } from '../inbox/useListMembers.js';
import { useLists } from '../inbox/useLists.js';
import { plural } from '../util/format.js';
import type { Guest } from './draft.js';
import { includesFold } from '../util/text.js';
import { guestOf, isInvitable, matchesInvitable, subtitleOf } from './invitable.js';
import styles from './GuestPicker.module.css';

/**
 * Where guests come from.
 *
 * **Lists + open Chats — the inverse of the mock, deliberately.** The mock
 * offers Lists + Contacts, having dropped its Chats source as redundant
 * ("everybody in the inbox already has a row in Contacts"). chat-emp has no
 * contacts pool at all, so the redundancy argument runs the other way and the
 * chats are the pool. This should be noted back in the mock rather than left to
 * look like drift.
 *
 * Both on to begin with, so the first search anybody runs reaches everything,
 * and either can be dropped to narrow it. A filter set rather than tabs: tabs
 * made the sources exclusive, so finding one person meant guessing which place
 * they were in and checking them one at a time.
 *
 * Lists first, because screening is not done here. It is done in the inbox, with
 * the real filters, and it arrives as a saved list — so the first thing on offer
 * is the work already done.
 */
const SOURCES = [
  { id: 'lists', label: 'Lists' },
  { id: 'chats', label: 'Chats' },
] as const;

type Source = (typeof SOURCES)[number]['id'];

/**
 * How long the server leg waits for the typing to stop.
 *
 * Only the name search crosses the network. The list names and the job titles
 * are filtered over what is already loaded and answer on the keystroke, which is
 * why this delay is not felt as latency — something always moves immediately.
 */
const SEARCH_DEBOUNCE_MS = 250;

/** How close to the end of the scroller the next page is asked for. */
const PAGE_AHEAD_PX = 120;

interface GuestPickerProps {
  /** Who is already coming, so they are not offered twice. */
  invitees: Guest[];
  /** Guests of the saved event being edited: shown, but not selectable. */
  alreadyInvited?: ReadonlySet<string>;
  client: StreamChat | null;
  onAdd: (guests: Guest[]) => void;
}

const NONE: ReadonlySet<string> = new Set();

/**
 * The drawer guests are taken out of.
 *
 * One job, since the guest list lives in the form column beside it: find people.
 * Holding both meant a pane that was a record and a tool at once, and the two
 * competed for the same height.
 *
 * The search is mixed-mode, driven by one box. Person-name search is
 * **server-side** — an inbox-query clause, so it reaches the whole set rather
 * than the page on screen. List names and job titles are filtered
 * **client-side** over what is loaded, and answer immediately.
 *
 * > Known limit, accepted: `InboxQueryResult` returns channel ids only and has
 * > no `search field: 'job'` clause, so job-title search reaches only loaded
 * > channels. Fine at current volumes; the fix is in the read model, not here.
 */
export function GuestPicker({ invitees, alreadyInvited = NONE, client, onAdd }: GuestPickerProps) {
  const { lists, loading: listsLoading } = useLists();
  const [sources, setSources] = useState<Source[]>(() => SOURCES.map((option) => option.id));
  const [query, setQuery] = useState('');

  const on = (id: Source) => sources.includes(id);
  const toggle = (id: Source) =>
    setSources((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

  const needle = query.trim().toLowerCase();

  /** The needle the server has been asked about. Trails `needle` by the debounce. */
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSettled(needle), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [needle]);

  const picked = useMemo(() => new Set(invitees.map((person) => person.id)), [invitees]);

  // ── the chats source ───────────────────────────────────────────────────────
  /**
   * The candidate set: open chats, newest first, a page at a time.
   *
   * Deliberately *unsearched*. It is what the client-side legs filter over, and
   * narrowing it server-side by name would leave the job-title leg with nothing
   * but name matches to look through — so typing a job title could never find
   * anybody, which is the one thing that leg exists to do.
   */
  const openQuery = useMemo<InboxQuery>(
    () => ({
      filters: [{ kind: 'status', phases: ['open'] }],
      grouping: { by: 'none' },
      sort: { key: 'last_message_at', dir: 'desc' },
    }),
    [],
  );
  const pool = useInboxList(openQuery, client);

  /**
   * The server leg: the same open filter plus the name clause, which reaches
   * people far below the loaded page. Passing a null client while the box is
   * empty is what keeps this from being a second copy of the query above.
   */
  const searchQuery = useMemo<InboxQuery>(
    () => ({
      filters: [
        { kind: 'status', phases: ['open'] },
        { kind: 'search', text: settled },
      ],
      grouping: { by: 'none' },
      sort: { key: 'last_message_at', dir: 'desc' },
    }),
    [settled],
  );
  const found = useInboxList(searchQuery, settled ? client : null);

  /** The server leg has not answered for what is currently typed. */
  const searching = needle !== settled || (settled !== '' && found.loading);

  const chatRows = useMemo(() => {
    // Server hits first so they win the dedup, then the loaded page. One row per
    // person, not per chat: the same jobseeker reached through two chats is one
    // guest, one invitation, one line on the roster.
    const byPerson = new Map<string, InboxItem>();
    for (const item of [...(settled ? found.items : []), ...pool.items]) {
      if (!isInvitable(item)) continue;
      if (picked.has(item.counterpart.id)) continue;
      if (!matchesInvitable(item, needle)) continue;
      if (!byPerson.has(item.counterpart.id)) byPerson.set(item.counterpart.id, item);
    }
    return [...byPerson.values()].sort(
      (a, b) => Date.parse(b.channel.lastActivityAt) - Date.parse(a.channel.lastActivityAt),
    );
  }, [found.items, pool.items, settled, needle, picked]);

  // ── the lists source ───────────────────────────────────────────────────────
  /**
   * Every list's members, resolved in one go.
   *
   * `useListMembers` resolves stored ids straight against Stream, chunked at
   * Stream's 30-per-page cap. Using it is not a preference: a list resolved by
   * intersecting it with the loaded inbox page silently drops every member below
   * that page — the bug that hook's header documents, and the one this pane
   * would otherwise reintroduce.
   *
   * One call over the union rather than one per list, because a hook cannot be
   * called in a loop over rows. Keyed on the ids, so it re-resolves when a list
   * changes and not while somebody types.
   */
  const memberIds = useMemo(() => [...new Set(lists.flatMap((list) => list.channelIds))], [lists]);
  const members = useListMembers(client, memberIds);

  /** The resolved members that can still be invited. It returns open OR ended by
   *  design, so the phase filter goes on top rather than into the hook. */
  const openMembers = useMemo(() => {
    const open = new Map<string, InboxItem>();
    for (const item of members.items) if (isInvitable(item)) open.set(item.channel.id, item);
    return open;
  }, [members.items]);

  const listRows = useMemo(
    () =>
      lists
        .filter((list) => includesFold(list.name, needle))
        .map((list) => {
          // One guest per person, in the order the list stores them.
          const guests = new Map<string, Guest>();
          for (const channelId of list.channelIds) {
            const item = openMembers.get(channelId);
            if (item && !guests.has(item.counterpart.id)) {
              guests.set(item.counterpart.id, guestOf(item));
            }
          }
          const addable = [...guests.values()].filter(
            (guest) => !picked.has(guest.id) && !alreadyInvited.has(guest.id),
          );
          return { list, held: list.channelIds.length, reachable: guests.size, addable };
        }),
    [lists, needle, openMembers, picked, alreadyInvited],
  );

  // ── what the pane says ─────────────────────────────────────────────────────
  /**
   * Whether the results need to say where each answer came from. Only past one
   * active source: with a single chip on, a heading over the whole list repeats
   * the chip already on screen an inch above it.
   */
  const grouped = sources.length > 1;

  /**
   * Still resolving. Guarded on emptiness so a re-query behind the scenes cannot
   * blank a pane that already has rows on it.
   */
  const loading =
    (on('chats') && (searching || (pool.loading && pool.items.length === 0))) ||
    (on('lists') && (listsLoading || (members.loading && members.items.length === 0)));

  const nothingFound =
    (!on('lists') || listRows.length === 0) && (!on('chats') || chatRows.length === 0);

  /** Page the candidate set as the pane is scrolled — never the whole inbox at once. */
  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!on('chats') || !pool.hasMore || pool.loadingMore) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < PAGE_AHEAD_PX) pool.loadMore();
  };

  return (
    <aside className={styles.panel} aria-label="Add guests">
      <div className={styles.head}>
        {/* The same word the field beside it uses. Two names for one thing on one
            screen ("Add jobseekers" here, "Guests" there) makes the reader work
            out that they are the same flow. */}
        <h2 className={styles.heading}>Add guests</h2>

        {/* The field is the container, not the input: the search icon sits inside
            the frame and an `<input>` cannot hold one. So the border, the radius
            and the focus ring belong to this div — the same shape the inbox
            search box uses. */}
        <div className={styles.field}>
          <span className={styles.searchIcon}>
            <Icon name="search" size={18} />
          </span>
          <input
            className={styles.searchInput}
            value={query}
            placeholder="Search names, jobs and lists"
            aria-label="Search for people and lists to invite"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {/*
         * Under the box, not inside it. Chips in the field work for a scope that
         * is either set or cleared, and stop working the moment one can be *off*
         * and still on screen: a greyed "Chats" inside a text field reads as
         * something the box is suggesting rather than as a switch.
         */}
        <div className={styles.chips}>
          {SOURCES.map((option) => {
            const active = on(option.id);
            return (
              <button
                key={option.id}
                type="button"
                className={active ? styles.chipOn : styles.chip}
                aria-pressed={active}
                title={active ? `Stop searching ${option.label}` : `Search ${option.label} too`}
                onClick={() => toggle(option.id)}
              >
                {option.label}
                {/* The cross only where there is something to take away. Off, the
                    chip is an offer rather than a thing to close. */}
                {active && <Icon name="close" size={12} />}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.options} onScroll={onScroll}>
        {sources.length === 0 ? (
          <p className={styles.none}>Nothing to search. Turn a chip back on to pick from it.</p>
        ) : (
          <>
            {on('lists') && listRows.length > 0 && (
              <>
                {grouped && <p className={styles.group}>Saved lists</p>}
                {listRows.map(({ list, held, reachable, addable }) => (
                  <button
                    key={list.id}
                    type="button"
                    className={styles.option}
                    // A list with nothing left to add stays on screen rather than
                    // vanishing — it is a real shelf and the employer is looking
                    // for it — but it says so instead of offering a press that
                    // changes nothing.
                    disabled={addable.length === 0}
                    onClick={() => onAdd(addable)}
                  >
                    <ListBadge list={list} />
                    <span className={styles.optionText}>
                      <span className={styles.optionName}>{list.name}</span>
                      <span className={styles.optionMeta}>
                        {/* The count is of who would actually be invited, which
                            is not what the list holds: a member whose chat has
                            ended is not offered, and a row promising more people
                            than the press delivers is the whole defect. */}
                        {reachable === 0
                          ? 'No open chats left'
                          : addable.length === 0
                            ? 'Everyone already invited'
                            : addable.length === held
                              ? plural(held, 'guest')
                              : `${addable.length} of ${held} left to add`}
                      </span>
                    </span>
                    {addable.length > 0 && <Icon name="plus" size={16} />}
                  </button>
                ))}
              </>
            )}

            {on('chats') && chatRows.length > 0 && (
              <>
                {grouped && <p className={styles.group}>Open chats</p>}
                {chatRows.map((item) => {
                  const locked = alreadyInvited.has(item.counterpart.id);
                  return (
                    <button
                      key={item.channel.id}
                      type="button"
                      className={styles.row}
                      aria-label={
                        locked
                          ? `${item.counterpart.name}, already invited`
                          : `Invite ${item.counterpart.name}`
                      }
                      disabled={locked}
                      onClick={() => onAdd([guestOf(item)])}
                    >
                      <Avatar
                        name={item.counterpart.name}
                        src={item.counterpart.avatarUrl}
                        locked={item.counterpart.locked}
                        size={26}
                      />
                      <span className={styles.optionText}>
                        <span className={styles.optionName}>{item.counterpart.name}</span>
                        <span className={styles.optionMeta}>{subtitleOf(item)}</span>
                      </span>
                      {locked ? (
                        <span className={styles.already}>
                          Already invited
                          <Icon name="check" size={14} />
                        </span>
                      ) : (
                        <Icon name="plus" size={16} />
                      )}
                    </button>
                  );
                })}
              </>
            )}

            {/* Last, and only once nothing is still resolving. Rendered while the
                server leg is in flight it would flash "no results" over results
                about to arrive. */}
            {loading ? (
              <p className={styles.none} role="status">
                Searching…
              </p>
            ) : (
              nothingFound && (
                <p className={styles.none}>
                  {needle ? 'Nothing by that name' : 'Everyone available is already invited'}
                </p>
              )
            )}
          </>
        )}
      </div>
    </aside>
  );
}
